import ModelPanel from "./components/Model/ModelPanel";
import Analytics from './components/Analytics/Analytics';
import Upload from './components/Upload/Upload';
import AlertsTable from './components/Alerts/AlertsTable';
import IncidentsPanel from './components/Incidents/Incidents';
import Dashboard from './components/Dashboard/Dashboard';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AuthGate, { RoleBadge, useAuth } from './AuthGate';
import { apiFetch } from './utils/apiClient';
import WebhookSettings from './components/Settings/WebhookSettings';
import UsersPanel from './components/Admin/UsersPanel';

// Nav tabs, each with the permission required to see it.
// Guest has only the view_* permissions, so it sees Dashboard/Alerts/
// Analytics/Incidents but NOT Upload/Model/Settings.
const NAV_ITEMS = [
  { label: 'Dashboard',      id: 'dashboard', perm: 'view_dashboard' },
  { label: 'Upload & Detect', id: 'upload',    perm: 'run_detection' },
  { label: 'Alerts',         id: 'alerts',    perm: 'view_alerts' },
  { label: 'Analytics',      id: 'analytics', perm: 'view_metrics' },
  { label: 'Incidents',      id: 'incidents', perm: 'view_incidents' },
  { label: 'Model',          id: 'model',     perm: 'switch_model' },
  { label: 'Settings',       id: 'settings',  perm: 'configure_webhooks' },
  { label: 'Users',          id: 'users',     perm: 'manage_users' },
];

const SEV_COLOR = { CRITICAL:'#ff0040', HIGH:'#ff3b4e', MEDIUM:'#f59e0b', LOW:'#10b981' };
const mono = { fontFamily:'JetBrains Mono,monospace' };

// AuthGate provides the auth context; AppContent consumes it via useAuth().
export default function App() {
  return (
    <AuthGate>
      <AppContent />
    </AuthGate>
  );
}

function AppContent() {
  const { canDo } = useAuth();
  const [page,    setPage]    = useState('dashboard');
  const [time,    setTime]    = useState(new Date());
  const [metrics, setMetrics] = useState(null);
  const [alerts,  setAlerts]  = useState([]);
  const [streamActive, setStreamActive] = useState(false);
  const pollIntervalRef = useRef(null);

  // Only the tabs this role is allowed to see.
  const visibleNav = NAV_ITEMS.filter(item => canDo(item.perm));

  // If the current page isn't visible to this role, fall back to the first
  // visible tab (e.g. a guest landing where Upload used to be).
  useEffect(() => {
    if (visibleNav.length && !visibleNav.some(n => n.id === page)) {
      setPage(visibleNav[0].id);
    }
  }, [visibleNav, page]);

  const refresh = useCallback(async () => {
    try {
      const [m, a] = await Promise.all([
        apiFetch('/metrics').then(r => r.json()),
        apiFetch('/alerts?limit=1000').then(r => r.json()),
      ]);
      setMetrics(m);
      setAlerts(a.alerts || []);
    } catch(e) { console.warn('API not reachable', e); }
  }, []);

  const startPolling = useCallback((fast = false) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    const ms = fast ? 2000 : 30000;
    pollIntervalRef.current = setInterval(refresh, ms);
  }, [refresh]);

  const handleStreamStart = useCallback(() => {
    setStreamActive(true);
    startPolling(true);
  }, [startPolling]);

  const handleStreamEnd = useCallback(async () => {
    setStreamActive(false);
    await refresh();
    startPolling(false);
  }, [refresh, startPolling]);

  const exportCSV = () => {
    if (!alerts.length) return;
    const h = ['timestamp','source_ip','attack_type','prediction','confidence','severity'];
    const csv = [h, ...alerts.map(a => h.map(k => a[k] ?? ''))].map(r => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
    Object.assign(document.createElement('a'), { href:url, download:'soc_alerts.csv' }).click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => { refresh(); }, []);                        // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [page, refresh]);
  useEffect(() => { startPolling(false); return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); }; }, [startPolling]);
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);

  const atk    = metrics?.total_attacks || 0;
  const tot    = metrics?.total_alerts  || 1;
  const threat = atk/tot > 0.7 ? 'HIGH' : atk/tot > 0.4 ? 'MEDIUM' : 'LOW';
  const tlCol  = SEV_COLOR[threat];

  return (
    <React.Fragment>
      <div style={{ background:'#0a0c10', minHeight:'100vh', color:'#e8eaf0', fontFamily:'IBM Plex Sans,sans-serif' }}>

        {/* ── Header ── */}
        <header style={{ background:'#0f1117', borderBottom:'1px solid #ffffff14', padding:'0 20px', display:'flex', alignItems:'center', justifyContent:'space-between', height:52, position:'sticky', top:0, zIndex:100 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <img
                src="/Favicon.png"
                alt="AEGIS SOC"
                style={{ width:48, height:48, borderRadius:6, objectFit:'cover' }}
              />
              <div>
                <div style={{ fontSize:14, fontWeight:600 }}>APT AEGIS SOC</div>
                <div style={{ fontSize:10, color:'#6b7280', ...mono }}> HYBRID LightGBM APT Detection</div>
              </div>
            </div>
            <div style={{ width:1, height:28, background:'#ffffff14' }} />
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, ...mono, color:'#10b981' }}>
              <div style={{ width:7, height:7, background:'#10b981', borderRadius:'50%' }} />
              SYSTEM OPERATIONAL
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ ...mono, fontSize:12, color:'#9ca3af' }}>
              {time.toLocaleString('en-US',{weekday:'short',month:'short',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false})}
            </span>
            <span style={{ padding:'3px 10px', borderRadius:4, fontSize:11, fontWeight:500, ...mono, background:`${tlCol}18`, color:tlCol, border:`1px solid ${tlCol}44` }}>
              THREAT: {threat}
            </span>
            <RoleBadge />
          </div>
        </header>

        {/* ── Nav ── */}
        <nav style={{ background:'#0f1117', borderBottom:'1px solid #ffffff14', padding:'0 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex' }}>
            {visibleNav.map((item) => (
              <button key={item.id} onClick={() => setPage(item.id)}
                style={{ padding:'10px 16px', fontSize:12, fontWeight:500, color:page===item.id?'#60a5fa':'#6b7280', background:'none', border:'none', borderBottom:`2px solid ${page===item.id?'#3b82f6':'transparent'}`, cursor:'pointer', letterSpacing:'.02em' }}>
                {item.label}
              </button>
            ))}
          </div>
          <div style={{ display:'flex', gap:8, padding:'6px 0' }}>
            {canDo('view_alerts') && (
              <button onClick={exportCSV} style={{ padding:'5px 12px', borderRadius:5, fontSize:11, cursor:'pointer', border:'1px solid #ffffff22', background:'#3b82f622', color:'#60a5fa', ...mono }}>↓ Export CSV</button>
            )}
            <button onClick={refresh}   style={{ padding:'5px 12px', borderRadius:5, fontSize:11, cursor:'pointer', border:'1px solid #ffffff22', background:'transparent', color:'#9ca3af', ...mono }}>↻ Refresh</button>
          </div>
        </nav>

        {/* ── Pages ── */}
        <main style={{ padding:'16px 20px' }}>
          <div style={{display:page==='dashboard'?'block':'none'}}>
            <Dashboard
              metrics={metrics}
              alerts={alerts}
              onUpdate={refresh}
              onStreamStart={handleStreamStart}
              onStreamEnd={handleStreamEnd}
            />
          </div>
          {canDo('run_detection') && (
            <div style={{display:page==='upload'?'block':'none'}}>
              <Upload onDone={()=>{ refresh(); setTimeout(refresh,2000); }} onDetectionComplete={()=>{ refresh(); setTimeout(refresh,2000); }} />
            </div>
          )}
          <div style={{display:page==='alerts'?'block':'none'}}>
            <AlertsTable alerts={alerts} />
          </div>
          <div style={{display:page==='analytics'?'block':'none'}}>
            <Analytics metrics={metrics} alerts={alerts} />
          </div>
          <div style={{display:page==='incidents'?'block':'none'}}>
            <IncidentsPanel />
          </div>
          {canDo('switch_model') && (
            <div style={{display:page==='model'?'block':'none'}}>
              <ModelPanel metrics={metrics} />
            </div>
          )}
          {canDo('configure_webhooks') && (
            <div style={{display:page==='settings'?'block':'none'}}>
              <WebhookSettings />
            </div>
          )}
          {canDo('manage_users') && (
            <div style={{display:page==='users'?'block':'none'}}>
              <UsersPanel />
            </div>
          )}
          {page==='workbench' && (
            <iframe
              src="/alert_tuning_workbench.html"
              style={{ width:'100%', height:'calc(100vh - 110px)', border:'none', borderRadius:8 }}
              title="Alert Tuning Workbench"
            />
          )}
        </main>

      </div>
    </React.Fragment>
  );
}